// Auth API — thin wrappers over the /api/v1/auth/* endpoints.
// Ported 1:1 from the old modules/auth.js fetch calls; contracts verified
// against http_api/auth.py (TokenResponse, *Request models).
//
// TokenResponse shape: { access_token, token_type, expires_in, user_id, display_name }
// register() may instead return { status: 'verification_required', email }.

import { api } from './client'
import type { RegisterResponse, TokenResponse } from '../types/api'

interface RegisterParams {
  segment?: string
  email: string
  displayName?: string | null
  password?: string | null
  inviteToken?: string | null
  accessToken?: string | null
  yandexClientId?: string | null
  timezone?: string | null
}

interface LoginParams {
  username: string
  password: string
  yandexClientId?: string | null
  timezone?: string | null
}

interface LoginCodeParams {
  email: string
  code: string
  yandexClientId?: string | null
  timezone?: string | null
}

interface InviteValidation {
  valid: boolean
  display_name?: string | null
}

export const authApi = {
  register: ({ email, displayName, password, inviteToken, accessToken, yandexClientId, timezone, segment }: RegisterParams) =>
    api.post<RegisterResponse>(
      '/auth/register',
      {
        email,
        segment,
        display_name: displayName || null,
        // Passwordless funnel (boards card «вход по ключу»): omit the password —
        // the backend accepts null and login happens via the email code.
        password: password || null,
        invite_token: inviteToken || null,
        access_link_token: accessToken || null,
        yandex_client_id: yandexClientId || null,
        timezone: timezone || null,
        // #4480: the build claims its users' content chain at creation
        feature: import.meta.env.VITE_APP_VARIANT || null,
      },
      { auth: false }
    ),

  // Logged-in user redeeming an access link they pasted/visited (vs. passing
  // it on register). Mirrors the bd-r3r PWA-only redemption path.
  redeemAccessLink: (token: string) =>
    api.post<{ applied?: boolean; status?: string }>('/access-links/redeem', { token }),

  login: ({ username, password, yandexClientId, timezone }: LoginParams) =>
    api.post<TokenResponse>(
      '/auth/login',
      { username, password, yandex_client_id: yandexClientId || null, timezone: timezone || null },
      { auth: false }
    ),

  // «Вход по ключу из почты»: ask for a one-time 6-digit code, then log in
  // with it. The request always resolves 200 (unknown emails are silent).
  requestLoginCode: ({ email }: { email: string }) =>
    api.post('/auth/request-login-code', { email }, { auth: false }),

  loginByCode: ({ email, code, yandexClientId, timezone }: LoginCodeParams) =>
    api.post<TokenResponse>(
      '/auth/login-code',
      { email, code, yandex_client_id: yandexClientId || null, timezone: timezone || null },
      { auth: false }
    ),

  verifyEmailCode: ({ email, code, timezone }: { email: string; code: string; timezone?: string | null }) =>
    api.post<TokenResponse>('/auth/verify-email-code', { email, code, timezone: timezone || null }, { auth: false }),

  verifyEmailToken: ({ token }: { token: string }) =>
    api.post<TokenResponse>('/auth/verify-email', { token }, { auth: false }),

  resendVerification: ({ email }: { email: string }) =>
    api.post('/auth/resend-verification', { email }, { auth: false }),

  forgotPassword: ({ email }: { email: string }) =>
    api.post('/auth/forgot-password', { email }, { auth: false }),

  // Backend accepts either `password` or `new_password`; send `new_password`
  // to match the old client and the ResetPasswordRequest validator.
  resetPassword: ({ token, password }: { token: string; password: string }) =>
    api.post<TokenResponse>('/auth/reset-password', { token, new_password: password }, { auth: false }),

  googleConfig: () => api.get<{ client_id: string }>('/auth/google/config', { auth: false }),
  google: ({ idToken, yandexClientId, timezone }: { idToken: string; yandexClientId?: string | null; timezone?: string | null }) =>
    // #4480: feature tag at creation (Google login mints the account on
    // first sign-in; later logins don't re-tag server-side)
    api.post<TokenResponse>(
      '/auth/google',
      {
        id_token: idToken,
        yandex_client_id: yandexClientId || null,
        timezone: timezone || null,
        feature: import.meta.env.VITE_APP_VARIANT || null,
      },
      { auth: false }
    ),

  yandexConfig: () => api.get<{ client_id: string }>('/auth/yandex/config', { auth: false }),
  yandex: ({ code, yandexClientId, timezone, segment }: { segment?: string; code: string; yandexClientId?: string | null; timezone?: string | null }) =>
    api.post<TokenResponse>(
      '/auth/yandex',
      {
        code,
        segment,
        yandex_client_id: yandexClientId || null,
        timezone: timezone || null,
        feature: import.meta.env.VITE_APP_VARIANT || null, // #4480, creation only
      },
      { auth: false }
    ),

  validateInvite: (token: string) => api.get<InviteValidation>(`/invite/${token}/validate`, { auth: false }),

  // Persist Yandex Metrica ClientID after OAuth-via-redirect login (bd-glg).
  // The redirect flow can't carry the value in the auth request, so we POST
  // it once the SPA has a session + the cached `_ym_uid` from localStorage.
  // `blocked` is set when getClientID never resolves (hard ad-block) so the
  // backend logs the irreducible capture floor (bd-ttr).
  persistYandexClientId: (yandexClientId: string, blocked = false) =>
    api.post('/profile/yandex-client-id', { yandex_client_id: yandexClientId, blocked }),
}
